/**
 * MarketplaceDownloadSuccess — Post-Stripe-checkout landing.
 *
 * Buyer arrives here from Stripe with ?session_id=cs_xxx. We use the
 * locally-persisted download_token (issued at checkout creation) to fetch
 * the download URL. If the token is missing (e.g. buyer opened success on a
 * different device) the page shows a friendly recovery hint.
 */
import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Download, CheckCircle2, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MarketplaceDownloadSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id') || '';
  const [state, setState] = useState({ status: 'loading', url: '', error: '', remaining: null });

  useEffect(() => {
    (async () => {
      let token = '';
      try { token = window.localStorage.getItem(`kk_dl_${sessionId}`) || ''; } catch { /* no-op */ }
      if (!token) {
        setState({ status: 'no_token', url: '', error: 'We could not find your download token on this device. Please check the email confirmation from Stripe or contact the seller with your session ID.', remaining: null });
        return;
      }
      // Poll up to 6 times (30s total) — Stripe webhook may lag a moment.
      for (let i = 0; i < 6; i++) {
        try {
          const res = await axios.get(`${API}/api/marketplace/downloads/${token}`);
          if (res.data?.download_url) {
            setState({ status: 'ready', url: res.data.download_url, error: '', remaining: res.data.downloads_remaining });
            return;
          }
          if (res.data?.error && !res.data.error.toLowerCase().includes('pending')) {
            setState({ status: 'error', url: '', error: res.data.error, remaining: null });
            return;
          }
        } catch (e) { /* keep retrying */ }
        await new Promise(r => setTimeout(r, 5000));
      }
      setState({ status: 'error', url: '', error: 'Your payment is still confirming. Please refresh this page in a minute.', remaining: null });
    })();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 text-white flex items-center justify-center px-4" data-testid="marketplace-download-success">
      <div className="max-w-md w-full bg-white/5 backdrop-blur-lg rounded-2xl border border-white/10 p-8 text-center">
        {state.status === 'loading' && (
          <>
            <Loader2 className="h-12 w-12 mx-auto text-emerald-400 animate-spin" />
            <h1 className="text-xl font-semibold mt-4">Confirming your payment…</h1>
            <p className="text-white/60 mt-2 text-sm">This usually takes a few seconds.</p>
          </>
        )}
        {state.status === 'ready' && (
          <>
            <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-400" />
            <h1 className="text-2xl font-semibold mt-4">Payment received.</h1>
            <p className="text-white/70 mt-2">Your download is ready. This link works up to {(state.remaining ?? 0) + 1} times.</p>
            <a href={state.url} target="_blank" rel="noopener noreferrer"
               className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold transition"
               data-testid="download-now-button">
              <Download className="h-5 w-5" /> Download now
              <ExternalLink className="h-4 w-4 opacity-60" />
            </a>
            <div className="mt-6 text-xs text-white/50">Keep this page open — you can re-download {state.remaining} more time{state.remaining === 1 ? '' : 's'}.</div>
          </>
        )}
        {(state.status === 'error' || state.status === 'no_token') && (
          <>
            <div className="h-14 w-14 mx-auto rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-2xl">!</div>
            <h1 className="text-xl font-semibold mt-4">One more step</h1>
            <p className="text-white/70 mt-2 text-sm">{state.error}</p>
            <div className="mt-6 flex gap-2 justify-center">
              <Button onClick={() => window.location.reload()} className="bg-emerald-500 hover:bg-emerald-400 text-black">Retry</Button>
              <Link to="/marketplace"><Button variant="outline" className="border-white/20 text-white bg-white/5 hover:bg-white/10">Back to Marketplace</Button></Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
